import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import ListPage from "../../../components/ListPage";
import EmptyValue from "../../../components/EmptyValue";
import useFinanceQuotations from "../../hooks/useFinanceQuotations";
import { DEFAULT_PAGE_SIZE } from "../../../constants/pagination";
import { FinanceTabs, NamePhoneCell, formatCurrency } from "../shared";
import { formatShortDate } from "../../../utils/format";

// Finance Quotations tab (§3). Row entity is `quotations`, not `userOrders` — a
// separate query, not the Orders one with a filter. Status is READ-ONLY here (§3
// DEFAULT): `quotations.status` is actively written by the eVital sync, so an editable
// dropdown (as the prototype has) would be silently overwritten on the next sync.
const PARAM = { page: "page" };

function FinanceQuotationsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Math.max(Number(searchParams.get(PARAM.page)) || 1, 1);

    const query = useFinanceQuotations({ page, size: DEFAULT_PAGE_SIZE });
    const pagination = query.data?.pagination ?? { page, size: DEFAULT_PAGE_SIZE, total: 0 };
    const rows = query.data?.quotations ?? [];

    const columns = useMemo(
        () => [
            { key: "id", label: "ID" },
            {
                key: "name",
                label: "Name",
                render: (row) => <NamePhoneCell mapUserPatient={row.mapUserPatient} />,
            },
            { key: "endDate", label: "End Date", render: (row) => formatShortDate(row.endDate) },
            {
                key: "partner",
                label: "Partner",
                render: (row) => row.partner?.name || <EmptyValue />,
            },
            {
                key: "financeTotalAmount",
                label: "Total Amount",
                align: "right",
                render: (row) => formatCurrency(row.financeTotalAmount),
            },
            {
                // §3 — same LOCKED relabel as Orders (§2): absolute rupees, never a percentage.
                key: "discount",
                label: "Discount (₹)",
                align: "right",
                render: (row) => formatCurrency(row.discount),
            },
            {
                key: "inventoryId",
                label: "Evital No",
                render: (row) => row.inventoryId || <EmptyValue />,
            },
            { key: "status", label: "Status" },
        ],
        []
    );

    return (
        <>
            <FinanceTabs />
            <ListPage
                testId="finance-quotations"
                title="Quotations"
                subtitle="Quotation-level finance view. Status is read-only here — it's actively written by the eVital sync."
                columns={columns}
                rows={rows}
                query={query}
                pagination={pagination}
                onPageChange={(nextPage) => {
                    const next = new URLSearchParams(searchParams);
                    next.set(PARAM.page, String(nextPage));
                    setSearchParams(next, { replace: true });
                }}
                emptyTitle="No quotations"
                emptyHint="No quotations found for this page."
            />
        </>
    );
}

export default FinanceQuotationsPage;
